import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Box, Button, Icon } from "@chakra-ui/react";
import { MeetingEventsFilters, useMeetingEventsQuery } from "features/MeetingEvents/hooks";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { DisplayAttendeesColumn } from "features/Resources/Meeting/Components/DisplayAttendees";
import CDS, { AdminMeetingTypeTag } from "design-system";
import { MeetingEvent } from "features/MeetingEvents/models";
import { formattedTimezone, getLocalTimezone, meetingStartEnd } from "helpers/time";
import { Text12, Text14 } from "components/Text";
import moment from "moment";
import BaseResourceTargetTag from "components/Tag/ResourceTargetTag/BaseResourceTargetTag";
import { IndividualSchedulingButtonBase } from "features/Resources/Meeting/Components/Button/Scheduling/IndividualSchedulingButton";
import { GroupSchedulingButtonBase } from "features/Resources/Meeting/Components/Button/Scheduling/GroupSchedulingButton";
import { BaseScheduleTableButton } from "features/Resources/Meeting/Components/Button/Scheduling/BaseScheduleButton";
import { TertiaryButton } from "components/Button";
import { LuCalendarPlus } from "react-icons/lu";

const columnHelper = createColumnHelper<MeetingEvent>();

function isUnscheduled(status: string): boolean {
  return ["pending_scheduling", "failed", "removed"].includes(status);
}

function WhenColumn({ meetingEvent }: { meetingEvent: MeetingEvent }) {
  const {
    meetingId, meetingType, status, startTime, endTime,
  } = meetingEvent;

  // If no meeting ID, can't schedule - just show time or empty
  if (!meetingId) {
    return <Text12>{meetingStartEnd(startTime, endTime)}</Text12>;
  }

  // For unscheduled meetings, show active scheduling button
  if (isUnscheduled(status)) {
    const isGroupMeeting = ["group_session_adhoc", "group_session_recurring"].includes(meetingType);
    const SchedulingButtonBase = isGroupMeeting ? GroupSchedulingButtonBase : IndividualSchedulingButtonBase;

    return (
      <SchedulingButtonBase
        meetingId={meetingId}
        renderButton={(onOpen) => <BaseScheduleTableButton onClick={onOpen} />}
      />
    );
  }

  // For scheduled meetings, show disabled button with time
  return (
    <TertiaryButton
      h="28px"
      rightIcon={<Icon as={LuCalendarPlus} boxSize="4" />}
      isDisabled
    >
      {meetingStartEnd(startTime, endTime)}
    </TertiaryButton>
  );
}

const columns: (
  filters: MeetingEventsFilters,
  onExpand: (item: MeetingEvent) => void
) => ColumnDef<MeetingEvent, any>[] = (filters, onExpand) => [
  { accessorKey: "__section" },
  columnHelper.accessor("meetingType", {
    id: "meetingType",
    header: "",
    enableSorting: false,
    cell: ({ getValue }) => <AdminMeetingTypeTag.IconBadge meetingType={getValue()} />,
    maxSize: 50,
    minSize: 50,
    size: 50,
    meta: {
      sx: {
        paddingLeft: "24px !important",
        paddingRight: 0,
      },
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "when",
    header: `When (${formattedTimezone(getLocalTimezone(), "short")})`,
    enableSorting: false,
    cell: (row) => <WhenColumn meetingEvent={row.getValue()} />,
  }),
  columnHelper.accessor((row) => row, {
    header: "Name",
    maxSize: 270,
    enableSorting: false,
    cell: (row) => (
      <Button variant="unstyled" width="fit-content" onClick={() => onExpand(row.getValue())}>
        <Text14
          wordBreak="break-word"
          textDecorationLine={row.getValue()?.status === "skipped" ? "line-through" : "none"}
        >
          {row.getValue()?.title}
        </Text14>
      </Button>
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "new_hires",
    header: "New Hire",
    enableSorting: false,
    cell: (row) => (
      row.getValue().newHireNames.map((name) => (
        <BaseResourceTargetTag key={name} roleName="new_hire" name={name} />
      ))
    ),
  }),
  columnHelper.accessor("attendees", {
    header: "Attendees",
    enableSorting: false,
    cell: ({ getValue }) => (
      <DisplayAttendeesColumn attendees={getValue()} />
    ),
  }),
  // columnHelper.accessor((row) => row, {
  //   id: "actions",
  //   header: "",
  //   enableSorting: false,
  //   cell: (row) => (
  //     <ViewDetailsButton item={row.getValue()} onExpand={onExpand} />
  //   ),
  // }),
].filter((c) => (filters.scheduled_status === "scheduled" ? (c.id !== "new_hires") : true));

interface MeetingEventsTableProps {
  filters: MeetingEventsFilters;
  onExpand: (item: MeetingEvent) => void;
}

function sectionForMeetingEvent(meetingEvent: MeetingEvent) {
  switch (meetingEvent.status) {
    case "pending_scheduling":
    case "failed":
    case "removed":
      return "Unscheduled";
    case "skipped":
      return "Skipped";
    case "scheduled":
    case "completed":
    case "cancelled":
    case "confirmed":
    case "tentative":
      return moment(meetingEvent.startTime).format("ddd, MM/D");
    default:
      return "Unscheduled";
  }
}

export default function MeetingEventsTable({ filters, onExpand }: MeetingEventsTableProps) {
  const { data, isLoading } = useMeetingEventsQuery(filters);
  const sectionedData = (data ?? []).map((m) => ({ ...m, __section: sectionForMeetingEvent(m) }));
  return (
    <ControlledDataTable<MeetingEvent>
      data={sectionedData}
      isLoading={isLoading}
      columns={columns(filters, onExpand)}
      emptyStateComponent={(
        <Box textAlign="center" p={4}>
          <CDS.Text16 color="chakra-subtle-text">
            No meetings found
          </CDS.Text16>
        </Box>
      )}
      sx={{ backgroundColor: "transparent" }}
    />
  );
}
